import type { AssemblyMateDefinition, MateEntityRef } from '../../assembly/definition'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyBrepIndexEntry,
  PuppyDisplayNodeBinding,
  PuppyPackageParseResult,
  PuppyParsedTopologyRuntimeAsset,
  PuppyProjectModelDocument,
} from './types'
import { createPuppyBodyTopologyAssetKey } from './bodyTopologyAssetKey'

/**
 * 断言集合中没有重复字符串。
 *
 * @param values 待检查的字符串集合。
 * @param label 错误上下文标签。
 */
function assertUniqueStrings(values: readonly string[], label: string): void {
  const seen = new Set<string>()
  values.forEach((value) => {
    if (seen.has(value)) {
      throw new Error(`PuppyCAD 离线包协议图存在重复 ${label}: ${value}`)
    }
    seen.add(value)
  })
}

/**
 * 建立唯一索引，遇到重复时立即拒绝。
 *
 * @param values 待索引数据。
 * @param readKey 读取唯一键的函数。
 * @param label 错误上下文标签。
 * @returns 唯一键到数据的索引。
 */
function createUniqueIndex<TValue>(
  values: readonly TValue[],
  readKey: (value: TValue) => string,
  label: string,
): Map<string, TValue> {
  const index = new Map<string, TValue>()
  values.forEach((value) => {
    const key = readKey(value)
    if (index.has(key)) {
      throw new Error(`PuppyCAD 离线包协议图存在重复 ${label}: ${key}`)
    }
    index.set(key, value)
  })
  return index
}

/**
 * 校验 display node 的 body 绑定是否能回到 BRep 与拓扑资产。
 *
 * @param input 校验输入。
 */
function assertDisplayNodeGraph(input: {
  readonly displayNode: PuppyDisplayNodeBinding
  readonly brepEntryByGeometryObjectId: ReadonlyMap<string, PuppyBrepIndexEntry>
  readonly topologyAssetByKey: ReadonlyMap<string, PuppyParsedTopologyRuntimeAsset>
  readonly bodyBindingIdSet: Set<string>
  readonly geometryObjectIdSet: Set<string>
}): void {
  const bindingGeometryObjectIds = input.displayNode.bodyBindings.map((binding) => binding.geometryObjectId)
  assertUniqueStrings(bindingGeometryObjectIds, `displayNodes.bodyBindings.geometryObjectId(${input.displayNode.displayNodeId})`)

  const declaredGeometryObjectIds = new Set(input.displayNode.geometryObjectIds)
  input.displayNode.bodyBindings.forEach((binding) => {
    if (!declaredGeometryObjectIds.has(binding.geometryObjectId)) {
      throw new Error(
        `PuppyCAD 离线包 display node bodyBindings 与 geometryObjectIds 不一致: ${input.displayNode.displayNodeId}/${binding.geometryObjectId}`,
      )
    }
    if (!input.brepEntryByGeometryObjectId.has(binding.geometryObjectId)) {
      throw new Error(`PuppyCAD 离线包 display body 缺少 BRep 索引: ${binding.geometryObjectId}`)
    }
    const topologyAssetKey = createPuppyBodyTopologyAssetKey(binding)
    if (!input.topologyAssetByKey.has(topologyAssetKey)) {
      throw new Error(`PuppyCAD 离线包 display body 缺少 topology-runtime 资产: ${binding.bodyBindingId}`)
    }
    const topologyAsset = input.topologyAssetByKey.get(topologyAssetKey)
    if (topologyAsset?.geometryObjectId !== binding.geometryObjectId) {
      throw new Error(
        `PuppyCAD 离线包 topology-runtime 几何对象与 display body 不一致: ${binding.bodyBindingId}`,
      )
    }
    input.bodyBindingIdSet.add(binding.bodyBindingId)
    input.geometryObjectIdSet.add(binding.geometryObjectId)
    assertUniqueStrings(binding.primitiveIds, `body primitiveId(${binding.bodyBindingId})`)
  })
}

/**
 * 校验 assembly node 对 display/body/BRep 的引用。
 *
 * @param node 当前装配节点。
 * @param displayNodeById display node 索引。
 * @param bodyBindingIdSet body 绑定 ID 集合。
 * @param geometryObjectIdSet BRep 几何对象 ID 集合。
 */
function assertAssemblyNodeBindingGraph(
  node: PuppyAssemblyNode,
  displayNodeById: ReadonlyMap<string, PuppyDisplayNodeBinding>,
  bodyBindingIdSet: ReadonlySet<string>,
  geometryObjectIdSet: ReadonlySet<string>,
): void {
  node.displayNodeIds.forEach((displayNodeId) => {
    if (!displayNodeById.has(displayNodeId)) {
      throw new Error(`PuppyCAD 离线包装配节点引用未知 display.glb 节点: ${node.nodeId}/${displayNodeId}`)
    }
  })
  node.bodyBindingIds.forEach((bodyBindingId) => {
    if (!bodyBindingIdSet.has(bodyBindingId)) {
      throw new Error(`PuppyCAD 离线包装配节点引用未知 bodyBindingId: ${node.nodeId}/${bodyBindingId}`)
    }
  })
  node.geometryObjectIds.forEach((geometryObjectId) => {
    if (!geometryObjectIdSet.has(geometryObjectId)) {
      throw new Error(`PuppyCAD 离线包装配节点引用未知 BRep 几何对象: ${node.nodeId}/${geometryObjectId}`)
    }
  })
}

/**
 * 判断 Mate 实体是否来自 imported BRep 拓扑。
 *
 * @param entity Mate 实体。
 * @returns 是否需要按 assembly 节点校验组件引用。
 */
function isTopologyMateEntity(entity: MateEntityRef): boolean {
  return Boolean(entity.topologyRef)
}

/**
 * 查找拓扑 Mate 实体的装配图引用错误。
 *
 * 设计说明：
 * 基准面、自动角度参考等 Mate 引用可能不是普通可移动组件，不能用节点集合硬拦。
 * 只有携带 `topologyRef` 的实体才代表从 imported BRep 拓扑拾取得到的组件边界，
 * 这类引用必须能回到 assembly.json 中的节点，否则拖动和求解会再次退回根节点。
 *
 * @param entity Mate 实体。
 * @param nodeIdSet assembly 节点集合。
 * @param nodeById assembly 节点索引。
 * @param bodyBindingIdSet 包内 bodyBindingId 集合。
 * @param context 错误上下文。
 * @returns 引用完整时返回 null，否则返回与严格图校验一致的错误消息。
 */
function findMateTopologyEntityReferenceError(
  entity: MateEntityRef,
  nodeIdSet: ReadonlySet<string>,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
  bodyBindingIdSet: ReadonlySet<string>,
  context: string,
): string | null {
  if (!isTopologyMateEntity(entity)) {
    return null
  }
  if (!nodeIdSet.has(entity.assemblyNodeId)) {
    return `PuppyCAD 离线包 Mate 拓扑实体引用未知装配节点: ${context}/${entity.assemblyNodeId}`
  }
  if (!nodeIdSet.has(entity.componentInstanceId)) {
    return `PuppyCAD 离线包 Mate 拓扑实体引用未知组件实例: ${context}/${entity.componentInstanceId}`
  }
  const topologyAssemblyNodeId = entity.topologyRef?.assemblyNodeId
  const topologyBodyBindingId = entity.topologyRef?.bodyBindingId
  if (!topologyBodyBindingId || !bodyBindingIdSet.has(topologyBodyBindingId)) {
    return `PuppyCAD 离线包 Mate 拓扑实体引用未知 bodyBindingId: ${context}/${topologyBodyBindingId ?? ''}`
  }

  const ownerNodeId = topologyAssemblyNodeId || entity.assemblyNodeId
  const ownerNode = nodeById.get(ownerNodeId)
  if (ownerNode && !ownerNode.bodyBindingIds.includes(topologyBodyBindingId)) {
    return `PuppyCAD 离线包 Mate 拓扑实体 bodyBindingId 与装配节点不一致: ${context}/${ownerNodeId}/${topologyBodyBindingId}`
  }
  if (topologyAssemblyNodeId && !nodeIdSet.has(topologyAssemblyNodeId)) {
    return `PuppyCAD 离线包 Mate 拓扑引用未知装配节点: ${context}/${topologyAssemblyNodeId}`
  }
  return null
}

/**
 * 使用已建立的装配图索引查找单条 Mate 的引用错误。
 *
 * first、second 与手动 referenceEntity 必须共享同一套判断；自动基准等不携带 topologyRef
 * 的实体仍沿用既有行为，不参加 imported BRep 节点硬校验。
 *
 * @param mate 待检查 Mate Definition。
 * @param nodeIdSet assembly 节点集合。
 * @param nodeById assembly 节点索引。
 * @param bodyBindingIdSet 包内 bodyBindingId 集合。
 * @returns 引用完整时返回 null，否则返回第一条确定性错误。
 */
function findPuppyMateDefinitionReferenceGraphErrorFromIndexes(
  mate: AssemblyMateDefinition,
  nodeIdSet: ReadonlySet<string>,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
  bodyBindingIdSet: ReadonlySet<string>,
): string | null {
  const entities: Array<readonly [string, MateEntityRef]> = [
    ['firstEntity', mate.firstEntity],
    ['secondEntity', mate.secondEntity],
  ]
  if (mate.referenceEntity) {
    entities.push(['referenceEntity', mate.referenceEntity])
  }

  for (const [entityName, entity] of entities) {
    const error = findMateTopologyEntityReferenceError(
      entity,
      nodeIdSet,
      nodeById,
      bodyBindingIdSet,
      `${mate.mateId}/${entityName}`,
    )
    if (error) {
      return error
    }
  }
  return null
}

/**
 * 查找单条 Puppy Mate 在当前 assembly.json 中的引用图错误。
 *
 * 该公开纯函数供历史包兼容层复用，保证“候选有效”与最终严格图校验含义完全一致。
 *
 * @param assembly assembly.json 文档。
 * @param mate 待检查 Mate Definition。
 * @returns 引用完整时返回 null，否则返回第一条确定性错误。
 */
export function findPuppyMateDefinitionReferenceGraphError(
  assembly: PuppyAssemblyDocument,
  mate: AssemblyMateDefinition,
): string | null {
  const nodeIdSet = new Set(assembly.nodes.map((node) => node.nodeId))
  const nodeById = new Map(assembly.nodes.map((node) => [node.nodeId, node]))
  const bodyBindingIdSet = new Set(assembly.nodes.flatMap((node) => node.bodyBindingIds))
  return findPuppyMateDefinitionReferenceGraphErrorFromIndexes(
    mate,
    nodeIdSet,
    nodeById,
    bodyBindingIdSet,
  )
}

/**
 * 校验 Mate 文档内拓扑实体引用。
 *
 * @param assembly assembly.json 文档。
 */
function assertMateReferenceGraph(assembly: PuppyAssemblyDocument): void {
  const nodeIdSet = new Set(assembly.nodes.map((node) => node.nodeId))
  const nodeById = new Map(assembly.nodes.map((node) => [node.nodeId, node]))
  const bodyBindingIdSet = new Set(assembly.nodes.flatMap((node) => node.bodyBindingIds))
  assembly.mates.mates.forEach((mate) => {
    const error = findPuppyMateDefinitionReferenceGraphErrorFromIndexes(
      mate,
      nodeIdSet,
      nodeById,
      bodyBindingIdSet,
    )
    if (error) {
      throw new Error(error)
    }
  })
}

/**
 * 校验 assembly.json 中全部 Mate 的拓扑引用闭合性。
 *
 * 导入与导出必须共享同一条图约束：凡是来自 imported BRep 的 Mate 实体，都必须引用
 * 当前 assembly 文档中的节点与 body binding，避免离线包在保存时成功、再次加载时才失败。
 *
 * @param assembly 待校验的最终 assembly.json 文档。
 */
export function validatePuppyAssemblyMateGraph(assembly: PuppyAssemblyDocument): void {
  assertMateReferenceGraph(assembly)
}

/**
 * 校验项目 occurrence 文档与 assembly.json 的跨文档绑定。
 *
 * 项目 occurrence 必须一对一绑定真实装配节点。这里不比较 transform，避免项目协同文档
 * 与当前场景位姿产生第二套权威；所有位姿仍只以 assembly.json 为准。
 *
 * @param input 装配语义和项目 occurrence 文档。
 * @throws 当 occurrence ID 重复、引用未知装配节点或多个 occurrence 绑定同一装配节点时。
 * @throws 当项目 occurrence 根绑定与 assembly.json 装配根不一致时。
 */
export function validatePuppyProjectModelGraph(input: {
  readonly assembly: PuppyAssemblyDocument
  readonly projectModel: PuppyProjectModelDocument
}): void {
  const assemblyNodeIdSet = new Set(input.assembly.nodes.map((node) => node.nodeId))
  const occurrenceIdSet = new Set<string>()
  const assemblyNodeIdByOccurrenceId = new Map<string, string>()
  const occurrenceIdByAssemblyNodeId = new Map<string, string>()

  input.projectModel.occurrences.forEach((occurrence) => {
    if (occurrenceIdSet.has(occurrence.occurrenceId)) {
      throw new Error(`PuppyCAD 项目 occurrence ID 重复：${occurrence.occurrenceId}`)
    }
    occurrenceIdSet.add(occurrence.occurrenceId)
    if (!assemblyNodeIdSet.has(occurrence.assemblyNodeId)) {
      throw new Error(
        `PuppyCAD 项目 occurrence 引用未知装配节点：${occurrence.occurrenceId}/${occurrence.assemblyNodeId}`,
      )
    }
    const existingOccurrenceId = occurrenceIdByAssemblyNodeId.get(occurrence.assemblyNodeId)
    if (existingOccurrenceId) {
      throw new Error(
        `PuppyCAD 项目 occurrence 重复绑定装配节点：${occurrence.assemblyNodeId}/${existingOccurrenceId}/${occurrence.occurrenceId}`,
      )
    }
    occurrenceIdByAssemblyNodeId.set(occurrence.assemblyNodeId, occurrence.occurrenceId)
    assemblyNodeIdByOccurrenceId.set(occurrence.occurrenceId, occurrence.assemblyNodeId)
  })

  const rootAssemblyNodeId = assemblyNodeIdByOccurrenceId.get(input.projectModel.rootOccurrenceId)
  if (rootAssemblyNodeId !== input.assembly.rootNodeId) {
    throw new Error(
      `PuppyCAD 项目 occurrence 根绑定与装配根不一致：${rootAssemblyNodeId ?? ''}/${input.assembly.rootNodeId}`,
    )
  }
}

/**
 * 校验 `.puppy` 解析结果的跨文件协议图。
 *
 * @param parsed 已完成单文件解析的离线包。
 */
export function validatePuppyPackageGraph(parsed: PuppyPackageParseResult): void {
  // manifest 中的数量字段只描述导出侧统计结果，不能作为导入阻断条件。
  // BRep 文件会按 sha 去重，多个装配节点或几何对象复用同一个 BRep 文件是合法结构。
  // 这里仅校验会影响场景、拓扑和配合恢复的跨文件引用闭合性。
  const displayNodeById = createUniqueIndex(
    parsed.brepIndex.displayNodes,
    (displayNode) => displayNode.displayNodeId,
    'displayNodeId',
  )
  const brepEntryByGeometryObjectId = createUniqueIndex(
    parsed.brepIndex.entries,
    (entry) => entry.geometryObjectId,
    'geometryObjectId',
  )
  const topologyAssetByKey = createUniqueIndex(
    parsed.topologyRuntime.assets,
    (asset) => createPuppyBodyTopologyAssetKey(asset),
    'topologyRuntime.bodyBindingId+geometryObjectId',
  )

  const bodyBindingIdSet = new Set<string>()
  const geometryObjectIdSet = new Set<string>()
  parsed.brepIndex.displayNodes.forEach((displayNode) => {
    assertDisplayNodeGraph({
      displayNode,
      brepEntryByGeometryObjectId,
      topologyAssetByKey,
      bodyBindingIdSet,
      geometryObjectIdSet,
    })
  })

  parsed.assembly.nodes.forEach((node) => {
    assertAssemblyNodeBindingGraph(node, displayNodeById, bodyBindingIdSet, geometryObjectIdSet)
  })

  validatePuppyAssemblyMateGraph(parsed.assembly)
  if (parsed.projectModel) {
    validatePuppyProjectModelGraph({
      assembly: parsed.assembly,
      projectModel: parsed.projectModel,
    })
  }
}
